import { Link } from "@/lib/router-compat";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Check, ArrowUpRight } from "lucide-react";
import type { FeatureKey } from "@/hooks/useFeatureAccess";
import { FEATURE_META } from "@/hooks/useFeatureAccess";

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: FeatureKey;
}

/**
 * In-app upsell modal shown when a user clicks a Pro/Agency/Enterprise feature
 * they don't have access to. Routes them to /pricing with a clear CTA.
 */
export const UpgradeModal = ({ open, onOpenChange, feature }: UpgradeModalProps) => {
  const meta = FEATURE_META[feature];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Badge className="bg-primary/10 text-primary border-primary/30 gap-1">
              <Sparkles className="h-3 w-3" />
              {meta.planLabel} feature
            </Badge>
          </div>
          <DialogTitle className="text-foreground text-xl">{meta.title}</DialogTitle>
          <DialogDescription className="text-muted-foreground pt-1">
            {meta.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 py-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            What you'll unlock
          </p>
          <ul className="space-y-2">
            {meta.benefits.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2.5 text-sm text-foreground">
                <div className="rounded-full bg-primary/10 p-0.5 mt-0.5 shrink-0">
                  <Check className="h-3 w-3 text-primary" />
                </div>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            Maybe later
          </Button>
          <Link to="/pricing" className="flex-1" onClick={() => onOpenChange(false)}>
            <Button className="w-full gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
              Upgrade to {meta.planLabel}
              <ArrowUpRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};
